import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { API } from "../../services/api";
import { useAuth } from "../../auth/AuthContext";
import { useRowEditing } from "../../hooks/useRowEditing";
import CommandeDetailModal from "../../components/CommandeDetailModal";
import SendToEhpadModal from "../../components/SendToEhpadModal";
import { useOptimisticProductionRows } from "../production/useOptimisticProductionRows";
import { productionSaveStatus } from "../production/SaveStatusBadge";
import {
    CLIENT_CLASSIFICATION_FILTERS,
    FilterField,
    PRODUCTION_PAGE_SIZE,
    PatientInfo,
    PrinterIcon,
    ProductSummary,
    RefreshIcon,
    STATUTS_HORS_PRODUCTION,
    StampCheck,
    matchesClassificationFilters,
    openEtiquettesPrintWindow,
    productCellStyle,
    productTone,
} from "../production/ProductionTableUi";
import ProductionSearchBar from "../production/ProductionSearchBar";
import { matchesProductionSearch, useDebouncedValue } from "../production/productionSearch";
import "./EtiquetageProduction.css";

const STATUT_LIVRAISON_OPTIONS = [
    { value: "",             label: "Non traité" },
    { value: "TRAITE",       label: "Traité" },
    { value: "ROBOT",        label: "→ Robot" },
    { value: "EN_COMMANDE",  label: "En commande" },
    { value: "EN_RUPTURE",   label: "En rupture" },
];

function ruptureMailContent(row, pharmacyName) {
    const dosage = row.dosage ? ` (${row.dosage})` : "";
    return `Bonjour,\n\nLe produit ${row.nom}${dosage} est en rupture, nous vous proposons de le remplacer par [...].\n\nVeuillez nous joindre une ordonnance avec la nouvelle prescription.\n\nBien à vous,\n${pharmacyName || "La Pharmacie"}`;
}

function normalizeCnoProductionRow(row) {
    const deliveryId = row.deliveryId ?? row.commandePatientId;
    return {
        ...row,
        id: `cno:${row.id}`,
        sourceType: "CNO",
        deliveryId,
        destination: "ETIQUETAGE",
        statut: row.statut === "PREVU" ? null : row.statut,
        classification: "CNO",
        classificationMedicament: "CNO",
        dateReception: row.deliveryDate ? `${row.deliveryDate}T00:00:00` : null,
        commandeId: `cno-delivery:${deliveryId}`,
        commandePatientId: `cno-patient:${row.patientId}`,
        ehpadNom: "CNO",
        ehpadCode: "CNO",
    };
}

function isCnoRowId(id) {
    return typeof id === "string" && id.startsWith("cno:");
}

export default function EtiquetageProduction() {
    const { user, selectedPharmacy, pharmacyId: currentPharmacyId } = useAuth();
    const robotTimers               = useRef({});
    const { rows, setRows, applyServerRows, appendServerRows, stageRow, flushRows } = useOptimisticProductionRows();
    const rowsRef = useRef(rows);
    rowsRef.current = rows;
    const scrollRef = useRef(null);
    const loadSeqRef = useRef(0);
    const loadingNextPageRef = useRef(false);
    const sentinelRef = useRef(null);
    const flushPatientRows = useCallback((commandePatientId) => {
        const ids = rowsRef.current
            .filter(row => row.commandePatientId === commandePatientId)
            .map(row => row.id);
        return flushRows(ids);
    }, [flushRows]);
    const { activeId: activeRowId, editingMap, select: selectRow, releaseActive: releaseActiveRow } = useRowEditing(
        currentPharmacyId,
        user?.email,
        { onBeforeUnlock: flushPatientRows }
    );
    const activeRowIdRef = useRef(null);
    activeRowIdRef.current = activeRowId;
    const [loading, setLoading]     = useState(false);
    const [tournees, setTournees]         = useState([]);
    const [tourneeFilter, setTourneeFilter] = useState("");
    const [classificationGroupFilter, setClassificationGroupFilter] = useState("");
    const [searchText, setSearchText] = useState("");
    const searchQuery = useDebouncedValue(searchText).trim();
    const [modalCommandeId, setModalCommandeId] = useState(null);
    const [ruptureMailRow, setRuptureMailRow]    = useState(null);
    const [currentPharmacy, setCurrentPharmacy] = useState(selectedPharmacy ?? null);
    const [page, setPage] = useState(0);
    const [hasMore, setHasMore] = useState(false);
    const [loadingNextPage, setLoadingNextPage] = useState(false);
    const [activeCnoPatientId, setActiveCnoPatientId] = useState(null);
    const [printSelectionMode, setPrintSelectionMode] = useState(false);
    const [selectedPrintIds, setSelectedPrintIds] = useState(() => new Set());
    const activeCnoPatientIdRef = useRef(null);
    activeCnoPatientIdRef.current = activeCnoPatientId;

    useEffect(() => {
        API.get("/ehpads/tournees").then(r => setTournees(r.data)).catch(() => {});
    }, []);

    const selectedPharmacyId = selectedPharmacy?.id ?? null;
    const selectedPharmacyName = selectedPharmacy?.name ?? null;

    useEffect(() => {
        const authPharmacy = selectedPharmacyId || selectedPharmacyName
            ? { id: selectedPharmacyId, name: selectedPharmacyName }
            : null;
        setCurrentPharmacy(authPharmacy);
        API.get("/admin/pharmacy", { noRedirectOn401: true })
            .then(r => setCurrentPharmacy(r.data ?? authPharmacy))
            .catch(() => setCurrentPharmacy(authPharmacy));
    }, [currentPharmacyId, selectedPharmacyId, selectedPharmacyName]);

    useEffect(() => () => {
        Object.values(robotTimers.current).forEach(clearTimeout);
        robotTimers.current = {};
    }, []);

    // Mise à jour de rowEditingBy dans les rows via les events WS
    useEffect(() => {
        setRows(prev => prev.map(r => {
            const editing = editingMap[r.commandePatientId];
            return editing !== undefined ? { ...r, rowEditingBy: editing ?? null } : r;
        }));
    }, [editingMap, setRows]);

    const loadProductionPage = useCallback((pageToLoad = 0, reset = false) => {
        const seq = reset ? ++loadSeqRef.current : loadSeqRef.current;
        if (reset) setLoading(true);
        else {
            loadingNextPageRef.current = true;
            setLoadingNextPage(true);
        }
        const params = new URLSearchParams({ destinationType: "ETIQUETAGE" });
        if (tourneeFilter) params.append("tournee",   tourneeFilter);
        if (classificationGroupFilter) params.append("classificationPatientGroup", classificationGroupFilter);
        if (searchQuery)   params.append("q",         searchQuery);
        params.append("page", String(pageToLoad));
        params.append("size", String(PRODUCTION_PAGE_SIZE));
        const cnoRequest = reset && pageToLoad === 0
            ? API.get("/cno/patients/etiquetage", { noRedirectOn401: true }).catch(() => ({ data: [] }))
            : Promise.resolve({ data: [] });
        Promise.all([API.get(`/medicaments/production?${params}`, { noRedirectOn401: true }), cnoRequest])
            .then(([r, cnoResponse]) => {
                if (seq !== loadSeqRef.current) return;
                const payload = r.data || {};
                const serverRows = Array.isArray(payload) ? payload : (payload.content || []);
                // Les CNO arrivent hors pagination : la recherche leur est appliquée ici.
                const cnoRows = (cnoResponse.data || [])
                    .map(normalizeCnoProductionRow)
                    .filter(row => matchesProductionSearch(row, searchQuery));
                const visibleRows = [
                    ...serverRows.filter(row => !STATUTS_HORS_PRODUCTION.has(row.statut)),
                    ...cnoRows,
                ];
                if (reset) applyServerRows(visibleRows);
                else appendServerRows(visibleRows);
                setPage(pageToLoad);
                setHasMore(Array.isArray(payload) ? false : !payload.last);
            })
            .catch(console.error)
            .finally(() => {
                if (seq !== loadSeqRef.current) return;
                if (reset) setLoading(false);
                else {
                    loadingNextPageRef.current = false;
                    setLoadingNextPage(false);
                }
            });
    }, [appendServerRows, applyServerRows, classificationGroupFilter, searchQuery, tourneeFilter]);

    const refresh = useCallback(async () => {
        const activePatientId = activeRowIdRef.current || activeCnoPatientIdRef.current;
        if (activePatientId) {
            const saved = await flushPatientRows(activePatientId);
            if (!saved) return;
        }
        setHasMore(false);
        setPage(0);
        loadingNextPageRef.current = false;
        loadProductionPage(0, true);
    }, [flushPatientRows, loadProductionPage]);

    const loadNextPage = useCallback(() => {
        if (loading || loadingNextPage || loadingNextPageRef.current || !hasMore) return;
        loadProductionPage(page + 1, false);
    }, [hasMore, loadProductionPage, loading, loadingNextPage, page]);

    const handleScroll = useCallback((event) => {
        const target = event.currentTarget;
        if (target.scrollHeight - target.scrollTop - target.clientHeight < 240) {
            loadNextPage();
        }
    }, [loadNextPage]);

    useEffect(() => {
        if (!hasMore || typeof window === "undefined") return undefined;
        const handleAnyScroll = () => {
            const sentinel = sentinelRef.current;
            if (!sentinel) return;
            if (sentinel.getBoundingClientRect().top <= window.innerHeight + 240) {
                loadNextPage();
            }
        };
        window.addEventListener("scroll", handleAnyScroll, true);
        return () => window.removeEventListener("scroll", handleAnyScroll, true);
    }, [hasMore, loadNextPage]);

    useEffect(() => {
        if (!hasMore || typeof IntersectionObserver === "undefined") return undefined;
        const sentinel = sentinelRef.current;
        if (!sentinel) return undefined;
        const observer = new IntersectionObserver(entries => {
            if (entries.some(entry => entry.isIntersecting)) loadNextPage();
        }, { root: scrollRef.current, rootMargin: "240px" });
        observer.observe(sentinel);
        return () => observer.disconnect();
    }, [hasMore, loadNextPage]);

    useEffect(() => { void refresh(); }, [refresh]);

    const patch = stageRow;
    const currentOperator = user?.username || user?.email || null;

    useEffect(() => {
        setSelectedPrintIds(current => {
            const availableIds = new Set(rows.map(row => row.id));
            const next = new Set([...current].filter(id => availableIds.has(id)));
            return next.size === current.size ? current : next;
        });
    }, [rows]);

    const selectedPrintRows = useMemo(
        () => rows.filter(row => selectedPrintIds.has(row.id)),
        [rows, selectedPrintIds]
    );

    const handlePrintClick = () => {
        if (!printSelectionMode) {
            setPrintSelectionMode(true);
            return;
        }
        if (selectedPrintRows.length === 0) return;
        const printPharmacy = currentPharmacy ?? selectedPharmacy;
        if (!printPharmacy?.name) return;
        openEtiquettesPrintWindow(selectedPrintRows, printPharmacy, "Étiquettes Étiquetage");
    };

    const cancelPrintSelection = () => {
        setPrintSelectionMode(false);
        setSelectedPrintIds(new Set());
    };

    const togglePrintSelection = id => {
        setSelectedPrintIds(current => {
            const next = new Set(current);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };

    const handleBilling = (id, checked) => {
        const now = checked ? new Date().toISOString() : null;
        const by = checked ? currentOperator : null;
        patch(
            id,
            { billingValidated: checked, billingUsername: by },
            { billingValidated: checked, billingUsername: by, billingHour: now }
        );
    };

    const handlePlanification = (id, checked) => {
        const now = checked ? new Date().toISOString() : null;
        const by = checked ? currentOperator : null;
        patch(
            id,
            { planificationValidated: checked, planificationValidatedUsername: by },
            { planificationValidated: checked, planificationValidatedAt: now, planificationValidatedBy: by }
        );
    };

    const handleStatut = (id, newValue) => {
        if (robotTimers.current[id]) {
            clearTimeout(robotTimers.current[id]);
            delete robotTimers.current[id];
        }
        if (newValue === "ROBOT") {
            patch(id, { destination: "ROBOT" }, { destination: "ROBOT", _pendingRobot: true });
            robotTimers.current[id] = setTimeout(() => {
                setRows(prev => prev.map(r => r.id === id ? { ...r, _pendingRobot: false } : r));
                delete robotTimers.current[id];
            }, 1500);
        } else {
            patch(id, { statut: newValue }, { statut: newValue || null, _pendingRobot: false });
        }
    };

    const handleEhpadWarned = (id, checked) => {
        patch(id, { ehpadWarnedIfOutOfStock: checked });
    };

    const handleEtiquette = (id, checked) => {
        const now = checked ? new Date().toISOString() : null;
        const by = checked ? currentOperator : null;
        patch(
            id,
            { productionValidated: checked, productionValidatedUsername: by },
            { productionValidated: checked, caisseHour: now, caisseUsername: by, statut: checked ? "TRAITE" : null }
        );
        if (isCnoRowId(id)) {
            flushRows([id]);
        }
    };

    const grouped = useMemo(() => {
        const map = new Map();
        rows.forEach(row => {
            const patientKey = row.commandePatientId
                ?? row.patientId
                ?? `${row.patientNom || ""}:${row.patientPrenom || ""}`;
            const groupKey = `${row.commandeId}:${patientKey}`;
            if (!map.has(groupKey)) map.set(groupKey, []);
            map.get(groupKey).push(row);
        });
        return [...map.values()];
    }, [rows]);

    const filtered = useMemo(() => {
        let result = grouped;
        if (tourneeFilter) {
            result = result.filter(g => g[0].ehpadTournee === tourneeFilter);
        }
        if (classificationGroupFilter) {
            result = result.filter(g => matchesClassificationFilters(g[0].classification, classificationGroupFilter, ""));
        }
        return result;
    }, [grouped, tourneeFilter, classificationGroupFilter]);

    const total = rows.length;
    const visibleCount = filtered.reduce((acc, g) => acc + g.length, 0);
    const selectedPrintCount = selectedPrintRows.length;

    return (
        <div className="rbt-page">
            <header className="rbt-header">
                <div className="rbt-header__title">
                    <h2>Étiquetage</h2>
                    <span className="rbt-count" title={visibleCount === total ? `${total} produit(s)` : `${visibleCount} produit(s) affiché(s) sur ${total}`}>
                        {visibleCount}{visibleCount === total ? "" : ` / ${total}`}
                    </span>
                </div>
                <div className="rbt-header__spacer" />
                {printSelectionMode && (
                    <span className={`rbt-selection-count${selectedPrintCount > 0 ? " is-active" : ""}`}>
                        {selectedPrintCount} sélectionné(s)
                    </span>
                )}
                <button
                    type="button"
                    className={`rbt-btn ${printSelectionMode ? "rbt-btn--primary" : "rbt-btn--label"}`}
                    onClick={handlePrintClick}
                    aria-disabled={printSelectionMode && selectedPrintCount === 0}
                    title={printSelectionMode && selectedPrintCount === 0 ? "Sélectionnez au moins un médicament" : "Imprimer étiquette"}
                >
                    <PrinterIcon />
                    <span>Imprimer étiquette</span>
                </button>
                {printSelectionMode && (
                    <button type="button" className="rbt-btn" onClick={cancelPrintSelection}>
                        Annuler
                    </button>
                )}
                <button type="button" className="rbt-btn" onClick={refresh}>
                    <RefreshIcon />
                    Actualiser
                </button>
            </header>

            <div className="rbt-filters">
                <FilterField label="Tournée" active={!!tourneeFilter}>
                    <select value={tourneeFilter} onChange={e => setTourneeFilter(e.target.value)}>
                        <option value="">Toutes</option>
                        {tournees.map(t => <option key={t.id} value={t.name}>{t.name}</option>)}
                    </select>
                </FilterField>
                <FilterField label="Type client" active={!!classificationGroupFilter}>
                    <select value={classificationGroupFilter} onChange={e => setClassificationGroupFilter(e.target.value)}>
                        {CLIENT_CLASSIFICATION_FILTERS.map(option => (
                            <option key={option.value || "ALL"} value={option.value}>{option.label}</option>
                        ))}
                    </select>
                </FilterField>
                <ProductionSearchBar value={searchText} onChange={setSearchText} withStatut />
            </div>

            <div ref={scrollRef} onScroll={handleScroll} className="rbt-table-card">
                {loading && <div className="rbt-empty">Chargement…</div>}
                {!loading && rows.length === 0 && <div className="rbt-empty">Aucun médicament.</div>}
                {!loading && rows.length > 0 && (
                    <>
                    <table className={`rbt-table etq-table${printSelectionMode ? " is-print-mode" : ""}`}>
                        <thead>
                            <tr>
                                {printSelectionMode && <th className="rbt-th rbt-th--center rbt-th--select">Sélect.</th>}
                                <th className="rbt-th rbt-th--info">Informations</th>
                                <th className="rbt-th" style={productCellStyle}>Produit</th>
                                <th className="rbt-th rbt-th--center rbt-th--qty">Qté</th>
                                <th className="rbt-th rbt-th--center rbt-th--check">Valid. factu{"\u00AD"}ration</th>
                                <th className="rbt-th rbt-th--center rbt-th--check">Valid. plani{"\u00AD"}fication</th>
                                <th className="rbt-th rbt-th--center etq-th--caisse">Collage étiquette et mise en caisse</th>
                                <th className="rbt-th etq-th--statut">Statut</th>
                                <th className="rbt-th etq-th--warned">EHPAD prévenue (rupture)</th>
                                <th className="rbt-th rbt-th--comment">Commentaire</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.map(group => {
                                const allBilling    = group.every(r => !!r.billingValidated);
                                const allPlanif     = group.every(r => !!r.planificationValidated);
                                const allProduction = group.every(r => !!r.productionValidated);
                                const billingRef = group.find(r => r.billingHour) || group[0];
                                const planifRef  = group.find(r => r.planificationValidatedAt) || group[0];
                                const groupComment = group[0].commentaryPlanificationEtiquetage || group[0].commentaryProductionEtiquetage || "";
                                const saveStatus   = productionSaveStatus(group);
                                const cpId        = group[0].commandePatientId;
                                const isCnoGroup  = group[0].sourceType === "CNO";
                                const rowEditedBy = isCnoGroup ? null : group[0].rowEditingBy || null;
                                const isActiveRow = isCnoGroup ? activeCnoPatientId === cpId : activeRowId === cpId;
                                const isLockedByCurrentUser = rowEditedBy === user?.email;
                                const groupPrintSelected = group.some(r => selectedPrintIds.has(r.id));
                                return group.map((row, idx) => (
                                    <EtiquetageRow
                                        key={row.id}
                                        row={row}
                                        isFirst={idx === 0}
                                        isLast={idx === group.length - 1}
                                        groupSize={group.length}
                                        groupBillingValidated={allBilling}
                                        groupBillingRef={billingRef}
                                        groupPlanifValidated={allPlanif}
                                        groupPlanifRef={planifRef}
                                        groupAllProduction={allProduction}
                                        saveStatus={saveStatus}
                                        groupComment={groupComment}
                                        onGroupBilling={checked => group.forEach(r => handleBilling(r.id, checked))}
                                        onGroupPlanif={checked => group.forEach(r => handlePlanification(r.id, checked))}
                                        onGroupComment={text => {
                                            const ids = group.map(r => r.id);
                                            ids.forEach(id => patch(id, { commentaryPlanificationEtiquetage: text }));
                                            return flushRows(ids);
                                        }}
                                        onEtiquette={checked => handleEtiquette(row.id, checked)}
                                        onStatut={handleStatut}
                                        onPatch={patch}
                                        onEhpadWarned={handleEhpadWarned}
                                        onOpenMail={row.sourceType === "CNO" ? null : setModalCommandeId}
                                        onRuptureMail={setRuptureMailRow}
                                        rowEditedBy={rowEditedBy}
                                        isActiveRow={isActiveRow}
                                        isLockedByCurrentUser={isLockedByCurrentUser}
                                        onGroupSelect={() => {
                                            if (row.sourceType === "CNO") {
                                                setActiveCnoPatientId(cpId);
                                                return Promise.resolve(true);
                                            }
                                            setActiveCnoPatientId(null);
                                            return selectRow(cpId);
                                        }}
                                        printSelectionMode={printSelectionMode}
                                        selectedForPrint={selectedPrintIds.has(row.id)}
                                        groupPrintSelected={groupPrintSelected}
                                        onTogglePrintSelection={() => togglePrintSelection(row.id)}
                                    />
                                ));
                            })}
                        </tbody>
                    </table>
                    <div ref={sentinelRef} className="rbt-sentinel">
                        {loadingNextPage && "Chargement des patients suivants…"}
                        {!loadingNextPage && (hasMore ? "Faites défiler pour charger la suite." : "Tous les patients chargés.")}
                    </div>
                    </>
                )}
            </div>

            {modalCommandeId && (
                <CommandeDetailModal
                    commandeId={modalCommandeId}
                    onClose={() => setModalCommandeId(null)}
                    onBeforeOpenValidation={releaseActiveRow}
                />
            )}

            {ruptureMailRow && (
                <SendToEhpadModal
                    commande={{
                        id: ruptureMailRow.commandeId,
                        ehpadNom: ruptureMailRow.ehpadNom,
                        ehpad: { emailsContact: ruptureMailRow.ehpadEmailsContact || [] }
                    }}
                    initialSujet={`Rupture de stock — ${ruptureMailRow.nom}`}
                    initialContenu={ruptureMailContent(ruptureMailRow, selectedPharmacy?.name)}
                    onClose={(sent) => {
                        if (sent) handleEhpadWarned(ruptureMailRow.id, true);
                        setRuptureMailRow(null);
                    }}
                />
            )}
        </div>
    );
}

function statutTone({ isPendingRobot, isEnRupture, isEnCommande, isTraite }) {
    if (isPendingRobot) return "robot";
    if (isEnRupture) return "rupture";
    if (isEnCommande) return "commande";
    if (isTraite) return "traite";
    return "default";
}

function EtiquetageRow({
    row, isFirst, isLast, groupSize,
    groupBillingValidated, groupBillingRef,
    groupPlanifValidated, groupPlanifRef,
    groupAllProduction,
    saveStatus,
    groupComment, onGroupComment,
    onGroupBilling, onGroupPlanif, onEtiquette,
    onStatut, onPatch, onEhpadWarned, onOpenMail, onRuptureMail,
    rowEditedBy = null, isActiveRow = false, isLockedByCurrentUser = false, onGroupSelect,
    printSelectionMode = false, selectedForPrint = false, groupPrintSelected = false, onTogglePrintSelection,
}) {
    const [comment, setComment] = useState(groupComment ?? "");
    const [commentDirty, setCommentDirty] = useState(false);
    const commentLockPromiseRef = useRef(null);
    // groupAllProduction pilote la teinte du groupe ; rowDone pilote la case individuelle
    const done       = !!groupAllProduction;
    const rowDone    = !!row.productionValidated;
    const isLockedByOther = !!rowEditedBy && !isActiveRow && !isLockedByCurrentUser;
    const isEnRupture    = row.statut === "EN_RUPTURE";
    const isEnCommande   = row.statut === "EN_COMMANDE";
    const isTraite       = row.statut === "TRAITE";
    const isPendingRobot = !!row._pendingRobot;
    const statutValue    = isPendingRobot ? "ROBOT" : (row.statut || "");
    const tone = productTone({ enRupture: isEnRupture, done: rowDone, planned: groupPlanifValidated });
    const rowEndClass = isLast ? " is-group-end" : "";

    useEffect(() => {
        if (!commentDirty) setComment(groupComment ?? "");
    }, [commentDirty, groupComment]);

    // Coche immédiatement, puis prend le verrou de la ligne ; annule la coche si le verrou est refusé.
    const toggleWithLock = (apply) => async (event) => {
        const checked = event.target.checked;
        apply(checked);
        if (!isActiveRow) {
            const ok = await onGroupSelect();
            if (!ok) apply(!checked);
        }
    };

    // Modifie la valeur puis prend le verrou de la ligne si besoin.
    const changeWithLock = (apply) => async (event) => {
        apply(event.target.value);
        if (!isActiveRow) await onGroupSelect();
    };

    const rowClassName = [
        "rbt-row",
        printSelectionMode ? "is-print-mode" : "",
        groupPrintSelected ? "is-print-selected" : "",
    ].filter(Boolean).join(" ");
    let infoStateClass = "";
    if (isActiveRow) infoStateClass = " is-active";
    else if (isLockedByOther) infoStateClass = " is-locked";

    return (
        <tr
            className={rowClassName}
            onClickCapture={event => {
                if (!printSelectionMode) return;
                event.preventDefault();
                event.stopPropagation();
                onTogglePrintSelection();
            }}
            title={printSelectionMode ? "Cliquer pour sélectionner cette étiquette" : undefined}
        >
            {printSelectionMode && (
                <td className={`rbt-select-cell rbt-center${rowEndClass}`}>
                    <input
                        type="checkbox"
                        className="rbt-checkbox"
                        aria-label={`Sélectionner l'étiquette ${row.nom || "produit"}`}
                        checked={selectedForPrint}
                        readOnly
                    />
                </td>
            )}

            {/* ── Informations (fusionnées par patient) ── */}
            {isFirst && (
                <td
                    rowSpan={groupSize}
                    onClick={onGroupSelect}
                    className={`rbt-info-cell rbt-merged${infoStateClass}`}
                    title={isLockedByOther ? `En cours d'édition par ${rowEditedBy}` : "Cliquer pour indiquer que vous éditez ce patient"}
                >
                    <PatientInfo
                        row={row}
                        saveStatus={saveStatus}
                        rowEditedBy={rowEditedBy}
                        isActiveRow={isActiveRow}
                        isLockedByOther={isLockedByOther}
                        onOpenMail={onOpenMail}
                    />
                </td>
            )}

            {/* ── Produit ── */}
            <td className={`rbt-product-cell rbt-tone--${tone}${rowEndClass}`} style={productCellStyle}>
                <ProductSummary row={row} printSelected={selectedForPrint} />
            </td>

            {/* ── Quantité ── */}
            <td className={`rbt-qty-cell rbt-tone--${tone}${rowEndClass}`}>×{row.quantite ?? "—"}</td>

            {/* ── Validation facturation (fusionnée par patient) ── */}
            {isFirst && (
                <td rowSpan={groupSize} className="rbt-merged rbt-center">
                    <StampCheck
                        label="Validation facturation"
                        checked={groupBillingValidated}
                        at={groupBillingRef?.billingHour}
                        by={groupBillingRef?.billingUsername}
                        disabled={isLockedByOther}
                        onChange={toggleWithLock(onGroupBilling)}
                    />
                </td>
            )}

            {/* ── Validation planification (fusionnée par patient) ── */}
            {isFirst && (
                <td rowSpan={groupSize} className="rbt-merged rbt-center">
                    <StampCheck
                        label="Validation planification"
                        checked={groupPlanifValidated}
                        at={groupPlanifRef?.planificationValidatedAt}
                        by={groupPlanifRef?.planificationValidatedBy}
                        disabled={isLockedByOther}
                        onChange={toggleWithLock(onGroupPlanif)}
                    />
                </td>
            )}

            {/* ── Collage étiquette et mise en caisse (par produit) ── */}
            <td className={`rbt-center${rowEndClass}`}>
                <StampCheck
                    label={`Collage étiquette et mise en caisse ${row.nom || "produit"}`}
                    checked={rowDone}
                    at={row.caisseHour}
                    by={row.caisseUsername}
                    disabled={isLockedByOther}
                    onChange={toggleWithLock(onEtiquette)}
                />
            </td>

            {/* ── Statut livraison ── */}
            <td className={rowEndClass.trim() || undefined}>
                {done ? (
                    <span className="etq-status-done">Traité ✓</span>
                ) : (
                    <div className="etq-status">
                        <div className="etq-status__controls">
                            <select
                                className={`etq-select etq-select--${statutTone({ isPendingRobot, isEnRupture, isEnCommande, isTraite })}`}
                                aria-label={`Statut ${row.nom || "produit"}`}
                                value={statutValue}
                                onChange={changeWithLock(value => onStatut(row.id, value))}
                                disabled={isLockedByOther}
                            >
                                {STATUT_LIVRAISON_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                            </select>
                            {isEnCommande && (
                                <input
                                    type="number"
                                    className="etq-qty-input"
                                    min={0}
                                    max={row.quantite ?? undefined}
                                    value={row.quantiteEnCommande ?? ""}
                                    disabled={isLockedByOther}
                                    onChange={changeWithLock(value => onPatch(row.id, { quantiteEnCommande: value === "" ? null : Number(value) }))}
                                    title="Quantité en commande"
                                    placeholder="qté"
                                />
                            )}
                        </div>
                        {isPendingRobot && <span className="etq-status__note">Transfert vers robot…</span>}
                    </div>
                )}
            </td>

            {/* ── EHPAD prévenue si rupture ── */}
            <td className={rowEndClass.trim() || undefined}>
                <div className="rbt-rupture-actions">
                    <input
                        type="checkbox"
                        className="rbt-checkbox"
                        aria-label="EHPAD prévenue de la rupture"
                        checked={!!row.ehpadWarnedIfOutOfStock}
                        disabled={!isEnRupture || isLockedByOther}
                        onChange={toggleWithLock(checked => onEhpadWarned(row.id, checked))}
                        title={isEnRupture ? "" : "Disponible uniquement en rupture"}
                    />
                    {row.ehpadWarnedIfOutOfStock && <span className="rbt-warned">Prévenue</span>}
                    {isEnRupture && (
                        <button
                            type="button"
                            className="rbt-btn-mini rbt-btn-mini--alert"
                            onClick={() => onRuptureMail(row)}
                            disabled={isLockedByOther}
                            title="Envoyer un mail de rupture à l'EHPAD"
                        >
                            Prévenir
                        </button>
                    )}
                </div>
            </td>

            {/* ── Commentaire (fusionné par patient) ── */}
            {isFirst && (
                <td rowSpan={groupSize} className="rbt-merged">
                    <textarea
                        className={`rbt-comment${comment ? " has-value" : ""}`}
                        rows={3}
                        cols={10}
                        value={comment}
                        aria-label="Commentaire d'étiquetage"
                        onFocus={() => {
                            commentLockPromiseRef.current = isActiveRow
                                ? Promise.resolve(true)
                                : Promise.resolve(onGroupSelect());
                        }}
                        onChange={e => {
                            setCommentDirty(true);
                            setComment(e.target.value);
                        }}
                        onBlur={async () => {
                            const lockAcquired = await (commentLockPromiseRef.current ?? Promise.resolve(isActiveRow));
                            commentLockPromiseRef.current = null;
                            if (commentDirty && (isActiveRow || lockAcquired || isLockedByCurrentUser)) {
                                await onGroupComment(comment);
                            } else if (commentDirty) {
                                setComment(groupComment ?? "");
                            }
                            setCommentDirty(false);
                        }}
                        disabled={isLockedByOther}
                        placeholder="Ajouter un commentaire…"
                    />
                </td>
            )}

        </tr>
    );
}
